import { parseBirthday } from './birthday';
import type { Graph } from './graph';

export const L = {
  cardW: 168,
  cardH: 246,
  coupleGap: 56,
  unitGap: 48,
  componentGap: 112,
  rowGap: 120,
  portraitY: 58,
  marginLeft: 156,
  marginRight: 80,
  marginTop: 72,
  diamond: 11,
  radius: 14,
  barOffsets: [0, -18, 18, -36, 36],
} as const;

export interface Position {
  /** Card centre x. */
  x: number;
  /** Card top y. */
  y: number;
  gen: number;
}

export interface Unit {
  members: string[];
  gen: number;
  comp: number;
  /** Centre x of the whole unit. */
  x: number;
  width: number;
}

export type Line =
  | { kind: 'couple'; a: string; b: string; d: string; mx: number; my: number }
  | {
      kind: 'child';
      parents: string[];
      child: string;
      d: string;
      /**
       * The couple this child belongs to. Usually the parents; for a child of just one partner
       * (an earlier relationship, a step-family) it is that parent plus their current partner.
       */
      household: string[];
      /** True when the child is a child of only one partner of `household`. */
      partial: boolean;
    };

export interface Layout {
  positions: Map<string, Position>;
  units: Unit[];
  rows: { gen: number; y: number; units: Unit[] }[];
  lines: Line[];
  width: number;
  height: number;
}

function unitWidth(n: number): number {
  return n * L.cardW + (n - 1) * L.coupleGap;
}

const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];

/** Sortable birth moment (year, then month and day when the text has them), or null when unknown. */
export function birthKey(p: { bornYear: number | null; born: string | null } | undefined): number | null {
  if (!p) return null;
  const year = p.bornYear;
  if (year === null) return null;
  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec((p.born ?? '').trim());
  if (iso && Number(iso[1]) === year) return year * 400 + Number(iso[2]) * 32 + Number(iso[3]);
  // numeric dates like 24/04/1971 (day first unless the other number is over 12)
  if (/^\d{1,2}[/.-]\d{1,2}[/.-]\d{4}$/.test((p.born ?? '').trim())) {
    const b = parseBirthday(p.born);
    if (b && b.year === year) return year * 400 + b.month * 32 + b.day;
  }
  const text = (p.born ?? '').toLowerCase();
  const m = MONTHS.findIndex((x) => new RegExp(`\\b${x}`).test(text));
  const day = m >= 0 ? Number(/\b(\d{1,2})\b(?!\d)/.exec(text.replace(/\b\d{4}\b/, ''))?.[1] ?? 0) : 0;
  return year * 400 + (m >= 0 ? m + 1 : 0) * 32 + day;
}

/** Weighted pool-adjacent-violators: non-decreasing fit of `vals`. */
export function isotonic(vals: number[], weights: number[]): number[] {
  const blocks: { sum: number; w: number; n: number }[] = [];
  for (let i = 0; i < vals.length; i++) {
    blocks.push({ sum: vals[i]! * weights[i]!, w: weights[i]!, n: 1 });
    while (blocks.length > 1) {
      const b = blocks[blocks.length - 1]!;
      const a = blocks[blocks.length - 2]!;
      if (a.sum / a.w <= b.sum / b.w) break;
      blocks.splice(blocks.length - 2, 2, { sum: a.sum + b.sum, w: a.w + b.w, n: a.n + b.n });
    }
  }
  const out: number[] = [];
  for (const b of blocks) for (let i = 0; i < b.n; i++) out.push(b.sum / b.w);
  return out;
}

function assignGenerations(g: Graph): { gen: Map<string, number>; comp: Map<string, number> } {
  const gen = new Map<string, number>();
  const comp = new Map<string, number>();
  let compIdx = 0;
  for (const start of g.order) {
    if (comp.has(start)) continue;
    const members: string[] = [];
    gen.set(start, 0);
    comp.set(start, compIdx);
    const queue = [start];
    while (queue.length) {
      const cur = queue.shift()!;
      members.push(cur);
      const cg = gen.get(cur)!;
      const visit = (n: string, ng: number) => {
        if (comp.has(n)) return;
        comp.set(n, compIdx);
        gen.set(n, ng);
        queue.push(n);
      };
      for (const n of g.partners.get(cur) ?? []) visit(n, cg);
      for (const n of g.parents.get(cur) ?? []) visit(n, cg - 1);
      for (const n of g.children.get(cur) ?? []) visit(n, cg + 1);
    }
    for (let pass = 0; pass < 12; pass++) {
      let changed = false;
      for (const id of members) {
        for (const c of g.children.get(id) ?? []) {
          if (gen.get(c)! < gen.get(id)! + 1) {
            gen.set(c, gen.get(id)! + 1);
            changed = true;
          }
        }
        for (const p of g.partners.get(id) ?? []) {
          const m = Math.max(gen.get(id)!, gen.get(p)!);
          if (gen.get(id) !== m || gen.get(p) !== m) {
            gen.set(id, m);
            gen.set(p, m);
            changed = true;
          }
        }
      }
      if (!changed) break;
    }
    const min = Math.min(...members.map((m) => gen.get(m)!));
    for (const m of members) gen.set(m, gen.get(m)! - min);
    compIdx++;
  }
  return { gen, comp };
}

function buildUnits(g: Graph, gen: Map<string, number>, comp: Map<string, number>): Unit[] {
  const seen = new Set<string>();
  const units: Unit[] = [];
  for (const id of g.order) {
    if (seen.has(id)) continue;
    const members: string[] = [];
    const stack = [id];
    seen.add(id);
    while (stack.length) {
      const cur = stack.pop()!;
      members.push(cur);
      for (const p of g.partners.get(cur) ?? []) {
        if (!seen.has(p) && gen.get(p) === gen.get(cur)) {
          seen.add(p);
          stack.push(p);
        }
      }
    }
    members.sort((a, b) => g.order.indexOf(a) - g.order.indexOf(b));
    if (members.length >= 3) {
      const counts = (m: string) => (g.partners.get(m) ?? []).filter((p) => members.includes(p)).length;
      const sorted = [...members].sort((a, b) => counts(b) - counts(a));
      const mid = sorted[0]!;
      const rest = sorted.slice(1);
      const left: string[] = [];
      const right: string[] = [];
      rest.forEach((m, i) => (i % 2 === 0 ? right : left).push(m));
      members.splice(0, members.length, ...left, mid, ...right);
    }
    units.push({
      members,
      gen: gen.get(id)!,
      comp: comp.get(id)!,
      x: 0,
      width: unitWidth(members.length),
    });
  }
  return units;
}

/** Lay the graph out: generations, couple units, ordering, x placement and connector lines. */
export function layout(g: Graph): Layout {
  const positions = new Map<string, Position>();
  if (g.people.size === 0) {
    return { positions, units: [], rows: [], lines: [], width: L.marginLeft + L.marginRight, height: L.marginTop * 2 };
  }
  const { gen, comp } = assignGenerations(g);
  const units = buildUnits(g, gen, comp);
  const unitOf = new Map<string, Unit>();
  for (const u of units) for (const m of u.members) unitOf.set(m, u);

  const maxGen = Math.max(...units.map((u) => u.gen));
  const rows: Unit[][] = Array.from({ length: maxGen + 1 }, () => []);
  for (const u of units) rows[u.gen]!.push(u); // already in creation order
  for (const row of rows) row.sort((a, b) => a.comp - b.comp);

  const rowMin = (row: Unit[]) => {
    // minimum centre-to-centre spacing offsets for a row
    const off: number[] = [];
    let cur = 0;
    row.forEach((u, i) => {
      if (i === 0) cur = u.width / 2;
      else {
        const prev = row[i - 1]!;
        const gap = prev.comp !== u.comp ? L.componentGap : L.unitGap;
        cur += prev.width / 2 + gap + u.width / 2;
      }
      off.push(cur);
    });
    return off;
  };
  const packRow = (row: Unit[]) => {
    const off = rowMin(row);
    row.forEach((u, i) => (u.x = off[i]!));
  };
  rows.forEach(packRow);

  const memberCentre = (u: Unit, m: string) => {
    const i = u.members.indexOf(m);
    return u.x - u.width / 2 + L.cardW / 2 + i * (L.cardW + L.coupleGap);
  };
  const cardX = (id: string) => memberCentre(unitOf.get(id)!, id);

  const parentMid = (id: string): number | null => {
    const ps = g.parents.get(id) ?? [];
    if (!ps.length) return null;
    // A child of one partner sits under the whole household (a step-family or an earlier relationship),
    // so half-siblings share a spot and are ordered by age like any other siblings.
    const house = ps.length === 1 ? [ps[0]!, ...(g.partners.get(ps[0]!) ?? [])] : ps;
    return house.reduce((s, p) => s + cardX(p), 0) / house.length;
  };
  // Generations of known ancestors above a person (0 = no parents in the tree).
  const depth = new Map<string, number>();
  const ancestry = (id: string, seen: ReadonlySet<string> = new Set()): number => {
    const known = depth.get(id);
    if (known !== undefined) return known;
    const ps = (g.parents.get(id) ?? []).filter((p) => !seen.has(p));
    const d = ps.length ? 1 + Math.max(...ps.map((p) => ancestry(p, new Set([...seen, id])))) : 0;
    depth.set(id, d);
    return d;
  };
  // A couple has two families pulling it in different directions. Follow the one with the longer
  // line of ancestors, so siblings stay together and the in-law's family hangs off to the side.
  const unitParentKey = (u: Unit): number | null => {
    const parented = u.members.filter((m) => parentMid(m) !== null);
    if (!parented.length) return null;
    const deepest = Math.max(...parented.map((m) => ancestry(m)));
    const anchors = parented.filter((m) => ancestry(m) === deepest);
    return anchors.reduce((s, m) => s + parentMid(m)!, 0) / anchors.length;
  };
  const unitChildKey = (u: Unit): number | null => {
    const kids = new Set<string>();
    for (const m of u.members) for (const c of g.children.get(m) ?? []) kids.add(c);
    if (!kids.size) return null;
    const xs = [...kids].map(cardX);
    return (Math.min(...xs) + Math.max(...xs)) / 2;
  };

  // Seed the sweeps with parents centred over their children, so a row's order is judged by where each
  // family really sits and not by the order the cards were created in.
  const resolve = (row: Unit[], desired: (number | null)[]) => {
    const off = rowMin(row);
    const vals = row.map((u, i) => (desired[i] ?? u.x) - off[i]!);
    const w = row.map((_, i) => (desired[i] === null ? 0.25 : 1));
    const fit = isotonic(vals, w);
    row.forEach((u, i) => (u.x = fit[i]! + off[i]!));
  };
  for (let r = maxGen - 1; r >= 0; r--) resolve(rows[r]!, rows[r]!.map(unitChildKey));

  // Barycentre sweeps for row order (and member order within couples).
  const orderMembers = (u: Unit) => {
    if (u.members.length !== 2) return;
    const [a, b] = u.members as [string, string];
    const pa = parentMid(a);
    const pb = parentMid(b);
    if (pa !== null && pb !== null && pa > pb) u.members = [b, a];
  };
  // Birth of the person who anchors a unit among their siblings (the partner with the longer ancestry).
  const unitBirth = (u: Unit): number | null => {
    const parented = u.members.filter((m) => (g.parents.get(m) ?? []).length > 0);
    if (!parented.length) return null;
    const deepest = Math.max(...parented.map((m) => ancestry(m)));
    const anchor = parented.find((m) => ancestry(m) === deepest)!;
    return birthKey(g.people.get(anchor));
  };
  const sortRow = (row: Unit[], key: (u: Unit) => number | null) => {
    const keyed = row.map((u) => ({ u, k: key(u) ?? u.x }));
    keyed.sort((p, q) => p.u.comp - q.u.comp || p.k - q.k);
    // Siblings sit at the same spot, so order them eldest first (left) to youngest (right). People
    // with no known birth keep their place; the others are sorted among the slots that are free.
    for (let i = 0; i < keyed.length; ) {
      let j = i + 1;
      while (j < keyed.length && keyed[j]!.u.comp === keyed[i]!.u.comp && Math.abs(keyed[j]!.k - keyed[i]!.k) < 0.5) j++;
      const slots = [];
      for (let t = i; t < j; t++) {
        const b = unitBirth(keyed[t]!.u);
        if (b !== null) slots.push({ t, b, e: keyed[t]! });
      }
      const sorted = [...slots].sort((p, q) => p.b - q.b);
      slots.forEach((sl, n) => (keyed[sl.t] = sorted[n]!.e));
      i = j;
    }
    row.splice(0, row.length, ...keyed.map((e) => e.u));
    // Keep each unit near where its family pulls it (not packed to the left edge), so the next row is
    // judged against positions in the same frame.
    resolve(row, keyed.map((e) => e.k));
  };
  for (let sweep = 0; sweep < 4; sweep++) {
    for (let r = 1; r <= maxGen; r++) {
      rows[r]!.forEach(orderMembers);
      sortRow(rows[r]!, (u) => unitParentKey(u) ?? unitChildKey(u));
    }
    // Going back up, a unit with parents keeps its place among its siblings; only a unit without parents
    // is placed above its children. (Following the children alone would pull a married-in sibling's
    // family between the siblings that have no children of their own.)
    for (let r = maxGen - 1; r >= 0; r--) sortRow(rows[r]!, (u) => (r > 0 ? unitParentKey(u) : null) ?? unitChildKey(u));
  }
  rows.forEach((row) => row.forEach(orderMembers));

  // X placement.
  for (let pass = 0; pass < 10; pass++) {
    if (pass % 2 === 0) {
      for (let r = 1; r <= maxGen; r++) {
        const row = rows[r]!;
        resolve(
          row,
          row.map((u) => {
            const ds: number[] = [];
            for (const m of u.members) {
              const pm = parentMid(m);
              if (pm !== null) ds.push(pm - (memberCentre(u, m) - u.x));
            }
            return ds.length ? ds.reduce((a, b) => a + b, 0) / ds.length : null;
          }),
        );
      }
    } else {
      for (let r = maxGen - 1; r >= 0; r--) {
        const row = rows[r]!;
        resolve(row, row.map(unitChildKey));
      }
    }
  }

  // Keep siblings together: in a couple, each partner faces their own siblings, so a spouse never sits
  // between two siblings but on the outer side of the one they married. Where both partners have
  // siblings on the same side, the one with more siblings (then the longer ancestry) stays next to them.
  const siblingUnits = (m: string, u: Unit): Unit[] => {
    const out = new Set<Unit>();
    for (const p of g.parents.get(m) ?? []) {
      for (const c of g.children.get(p) ?? []) {
        const cu = unitOf.get(c);
        if (c !== m && cu && cu !== u) out.add(cu);
      }
    }
    return [...out];
  };
  for (const u of units) {
    if (u.members.length !== 2) continue;
    const [a, b] = u.members as [string, string];
    const info = (m: string) => {
      const sibs = siblingUnits(m, u);
      if (!sibs.length) return null;
      const mean = sibs.reduce((s, x) => s + x.x, 0) / sibs.length;
      return { dir: mean < u.x ? -1 : 1, count: sibs.length };
    };
    const ia = info(a);
    const ib = info(b);
    let first: string | null = null; // the member that goes on the left
    if (ia && ib) {
      if (ia.dir !== ib.dir) first = ia.dir < 0 ? a : b;
      else {
        const winner = ia.count !== ib.count ? (ia.count > ib.count ? a : b) : ancestry(a) >= ancestry(b) ? a : b;
        first = (winner === a ? ia : ib).dir < 0 ? winner : winner === a ? b : a;
      }
    } else if (ia || ib) {
      const m = ia ? a : b;
      first = (ia ?? ib)!.dir < 0 ? m : m === a ? b : a;
    } else if (ancestry(a) !== ancestry(b)) {
      // No siblings either way: face the parents of the partner with the longer line of ancestors.
      const anchor = ancestry(a) > ancestry(b) ? a : b;
      const px = parentMid(anchor);
      if (px !== null) first = px < u.x ? anchor : anchor === a ? b : a;
    }
    if (first) u.members = first === a ? [a, b] : [b, a];
  }

  // Shift so the leftmost card sits on the left margin.
  let minLeft = Infinity;
  let maxRight = -Infinity;
  for (const u of units) {
    minLeft = Math.min(minLeft, u.x - u.width / 2);
    maxRight = Math.max(maxRight, u.x + u.width / 2);
  }
  const shift = L.marginLeft - minLeft;
  for (const u of units) u.x += shift;
  const width = maxRight + shift + L.marginRight;
  const rowY = (gn: number) => L.marginTop + gn * (L.cardH + L.rowGap);
  const height = rowY(maxGen) + L.cardH + L.marginTop;

  for (const u of units) {
    for (const m of u.members) positions.set(m, { x: memberCentre(u, m), y: rowY(u.gen), gen: u.gen });
  }

  const lines: Line[] = [];

  // Couple lines (adjacent partners in a unit).
  for (const u of units) {
    for (let i = 0; i + 1 < u.members.length; i++) {
      const a = u.members[i]!;
      const b = u.members[i + 1]!;
      if (!(g.partners.get(a) ?? []).includes(b)) continue;
      const pa = positions.get(a)!;
      const pb = positions.get(b)!;
      const y = pa.y + L.portraitY;
      lines.push({
        kind: 'couple',
        a,
        b,
        d: `M ${pa.x + L.cardW / 2} ${y} H ${pb.x - L.cardW / 2}`,
        mx: (pa.x + pb.x) / 2,
        my: y,
      });
    }
  }

  // Child connectors, grouped by exact parent set.
  interface Group {
    parents: string[];
    kids: string[];
    ox: number;
    oy: number;
    barY: number;
    lo: number;
    hi: number;
    offset: number;
    gap: number;
  }
  const groups = new Map<string, Group>();
  for (const id of g.order) {
    const ps = [...(g.parents.get(id) ?? [])].sort();
    if (!ps.length) continue;
    const key = ps.join('|');
    let grp = groups.get(key);
    if (!grp) {
      const pp = ps.map((p) => positions.get(p)!);
      let ox: number;
      let oy: number;
      const adjacent =
        ps.length === 2 &&
        unitOf.get(ps[0]!) === unitOf.get(ps[1]!) &&
        Math.abs(unitOf.get(ps[0]!)!.members.indexOf(ps[0]!) - unitOf.get(ps[0]!)!.members.indexOf(ps[1]!)) === 1 &&
        (g.partners.get(ps[0]!) ?? []).includes(ps[1]!);
      if (adjacent) {
        ox = (pp[0]!.x + pp[1]!.x) / 2;
        oy = pp[0]!.y + L.portraitY;
      } else {
        ox = pp.reduce((s, p) => s + p.x, 0) / pp.length;
        oy = Math.max(...pp.map((p) => p.y)) + L.cardH;
      }
      grp = { parents: ps, kids: [], ox, oy, barY: 0, lo: ox, hi: ox, offset: 0, gap: 0 };
      groups.set(key, grp);
    }
    grp.kids.push(id);
  }
  const byGap = new Map<number, Group[]>();
  for (const grp of groups.values()) {
    const childGen = Math.min(...grp.kids.map((k) => positions.get(k)!.gen));
    grp.gap = childGen;
    const xs = grp.kids.map((k) => positions.get(k)!.x);
    grp.lo = Math.min(grp.ox, ...xs);
    grp.hi = Math.max(grp.ox, ...xs);
    (byGap.get(childGen) ?? byGap.set(childGen, []).get(childGen)!).push(grp);
  }
  for (const [childGen, list] of byGap) {
    list.sort((a, b) => a.ox - b.ox);
    const placed: Group[] = [];
    for (const grp of list) {
      let chosen = 0;
      for (const off of L.barOffsets) {
        const clash = placed.some((p) => p.offset === off && !(p.hi < grp.lo - 4 || p.lo > grp.hi + 4));
        if (!clash) {
          chosen = off;
          break;
        }
        chosen = L.barOffsets[L.barOffsets.length - 1]!;
      }
      grp.offset = chosen;
      grp.barY = rowY(childGen) - L.rowGap / 2 + chosen;
      placed.push(grp);
    }
  }
  const partnerInCouple = new Map<string, string>();
  for (const l of lines) {
    if (l.kind !== 'couple') continue;
    if (!partnerInCouple.has(l.a)) partnerInCouple.set(l.a, l.b);
    if (!partnerInCouple.has(l.b)) partnerInCouple.set(l.b, l.a);
  }
  const r = L.radius;
  for (const grp of groups.values()) {
    for (const kid of grp.kids) {
      const kp = positions.get(kid)!;
      const cx = kp.x;
      const top = kp.y;
      let d: string;
      if (Math.abs(cx - grp.ox) < 0.5) {
        d = `M ${grp.ox} ${grp.oy} V ${top}`;
      } else {
        const dir = cx > grp.ox ? 1 : -1;
        const rr = Math.min(r, Math.abs(cx - grp.ox) / 2, Math.max(2, grp.barY - grp.oy), Math.max(2, top - grp.barY));
        d =
          `M ${grp.ox} ${grp.oy} V ${grp.barY - rr} Q ${grp.ox} ${grp.barY} ${grp.ox + dir * rr} ${grp.barY} ` +
          `H ${cx - dir * rr} Q ${cx} ${grp.barY} ${cx} ${grp.barY + rr} V ${top}`;
      }
      const partner = grp.parents.length === 1 ? partnerInCouple.get(grp.parents[0]!) : undefined;
      const household = partner ? [grp.parents[0]!, partner].sort() : grp.parents;
      lines.push({ kind: 'child', parents: grp.parents, child: kid, d, household, partial: partner !== undefined });
    }
  }

  const outRows = rows.map((row, i) => ({ gen: i, y: rowY(i), units: row }));
  return { positions, units, rows: outRows, lines, width, height };
}

/** Does this line touch the selected person (their parents or children)? */
export function lineTouches(line: Line, selectedId: string | null): boolean {
  if (!selectedId) return false;
  if (line.kind === 'couple') return false;
  return line.child === selectedId || line.parents.includes(selectedId);
}

/** How many distinct family colours the canvas palette provides. */
export const FAMILY_COLOURS = 8;

/** Key shared by every connector of one household (a couple, or a lone parent) down to its children. */
export const familyKey = (parents: readonly string[]) => [...parents].sort().join('|');

/**
 * Colour index (0..n-1) for each household of child connectors, keyed by `familyKey(line.household)`.
 * A child of only one partner shares the colour of their parent's household, drawn dashed.
 * Two families get different colours when they share a person, or when their connectors
 * could overlap on the same row gap, so neighbouring lines stay easy to tell apart.
 */
export function familyColours(lay: Layout, n: number = FAMILY_COLOURS): Map<string, number> {
  interface Fam {
    key: string;
    ids: Set<string>;
    gen: number;
    lo: number;
    hi: number;
  }
  const fams = new Map<string, Fam>();
  for (const l of lay.lines) {
    if (l.kind !== 'child') continue;
    const key = familyKey(l.household);
    const kid = lay.positions.get(l.child);
    if (!kid) continue;
    let f = fams.get(key);
    if (!f) {
      f = { key, ids: new Set(l.household), gen: kid.gen, lo: Infinity, hi: -Infinity };
      fams.set(key, f);
    }
    f.ids.add(l.child);
    for (const id of [...l.household, l.child]) {
      const x = lay.positions.get(id)?.x;
      if (x !== undefined) {
        f.lo = Math.min(f.lo, x);
        f.hi = Math.max(f.hi, x);
      }
    }
  }
  const out = new Map<string, number>();
  const placed: Fam[] = [];
  let next = 0;
  for (const f of fams.values()) {
    const taken = new Set<number>();
    for (const p of placed) {
      const shares = [...f.ids].some((id) => p.ids.has(id));
      const overlaps = p.gen === f.gen && !(p.hi < f.lo - 8 || p.lo > f.hi + 8);
      if (shares || overlaps) taken.add(out.get(p.key)!);
    }
    // Round-robin from the last colour used, so all n hues appear before any repeats and
    // unrelated families far apart are unlikely to look related.
    let c = next;
    for (let i = 0; i < n && taken.has(c); i++) c = (c + 1) % n;
    out.set(f.key, c);
    next = (c + 1) % n;
    placed.push(f);
  }
  return out;
}
